import { OnInit, Component, inject } from '@angular/core';
import { ListService } from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import {
  InterventionCreateDto,
  InterventionUpdateDto,
} from '../proxy/geographic/dtos/intervention';
import { INTERVENTION_CONFIG, INTERVENTION_FIELDS } from './intervention.config';
import {
  GenericForm,
  BaseHierarchicalCrudComponent,
  ICrudService,
  ZnHierarchicalViewComponent,
  IActionsSchema,
  resolveActions,
  normalizePropertyPrefilterQuery,
} from '@zendra/shared';
import { InterventionPlanningOrchestratorService } from 'orchestrator';
import { GuardedDeletionService } from 'projects/orchestrator/src/lib/services/guarded-deletion.service';
import {
  GetInterventionProjectionListInputDto,
  InterventionProjectionDto,
  InterventionProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/interventions/models';

@Component({
  selector: 'app-intervention',
  imports: [
    GenericForm,
    ZnHierarchicalViewComponent,
  ],
  providers: [ListService, ConfirmationService],
  templateUrl: './intervention.html',
  styleUrl: './intervention.scss',
})
export class Intervention extends BaseHierarchicalCrudComponent<
  InterventionProjectionDto,
  InterventionCreateDto,
  InterventionUpdateDto,
  InterventionProjectionExcelDownloadDto
> implements OnInit {
  private orchestratorService = inject(InterventionPlanningOrchestratorService);
  private readonly guardedDeletion = inject(GuardedDeletionService);

  service: ICrudService<
    InterventionProjectionDto,
    InterventionCreateDto,
    InterventionUpdateDto,
    InterventionProjectionExcelDownloadDto
  > = {
    create: input => this.orchestratorService.create(input),
    update: (id, input) => this.orchestratorService.update(id, input),
    deleteByIds: ids => this.guardedDeletion.deleteByIds('interventions', ids),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'interventionId',
    ),
    getList: (query: GetInterventionProjectionListInputDto) =>
      this.orchestratorService.getList(
        normalizePropertyPrefilterQuery(query),
      ),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.orchestratorService.getListAsExcelFile(
        normalizePropertyPrefilterQuery(input),
      ),
  };

  config = INTERVENTION_CONFIG;
  fields = INTERVENTION_FIELDS;
  actions: IActionsSchema | null = null;

  ngOnInit(): void {
    if (!this.config.actions || !this.config.actionIds) {
      return;
    }

    this.actions = resolveActions(
      this.config.actions,
      {
        [this.config.actionIds.processInterventionTrigger]:
          () => this.processIntervention(),
        [this.config.actionIds.unprocessInterventionTrigger]:
          () => this.unprocessIntervention(),
      },
    );
  }

  protected prepareUpdateDto(data: any, selected: InterventionProjectionDto): InterventionUpdateDto {
    return {
      interventionDate: data.interventionDate,
      workedArea: data.workedArea,
      isInterventionCompleted: selected.isInterventionCompleted ?? data.isInterventionCompleted ?? false,
      automaticallyProcessed: data.automaticallyProcessed,
      standingArea: data.standingArea,
      rotationId: data.rotationId,
      interventionTypeId: data.interventionTypeId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'Intervention';
  }

  private processIntervention(): void {
    this.runBulkAction(ids =>
      this.orchestratorService.process({ ids })
    );
  }

  private unprocessIntervention(): void {
    const ids = this.selectedRows;


    if (!ids?.length) {
      return;
    }

    const standId = ids[0];

    this.orchestratorService.unprocess(standId).subscribe({
      next: () => {
        this.toaster.success(this.localizationService.instant('::MessageSaveSuccess'));
      },
      error: (err) => {
        console.error(err);
      }
    });
  }
}
